<template>
  <v-card
    class="pa-5 ml-10 mr-10 mt-5"
    :title="t('views.licence.table.title')">
    <template v-slot:append>
      <div class="paymentMode">
        <button @click="mode='monthly'" class="modeButton" :class="mode === 'monthly' ? 'active' : 'not-active'">
          {{ $t("views.licence.other.monthly") }}
        </button>
        <button @click="mode='yearly'" class="modeButton" :class="mode === 'yearly' ? 'active' : 'not-active'">
          {{ $t("views.licence.other.yearly") }}
        </button>
      </div>
    </template>
    <v-card-text>
      <v-data-table
        hide-default-footer
        items-per-page="20"
        class="border-table"
        density="compact"
        :headers="headers"
        hide-no-data
        item-value="id"
        :items="items">
        <template v-slot:item.slots="{ item }">
          <span>{{ item.slots }}</span>
          <span v-if="item.monthEnd"> (+{{ item.monthEnd }} {{$t("views.licence.review.until", {date: (new Date(props.next_billing_cycle_anchor * 1000)).toLocaleDateString()})}})</span>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text>
      <v-row class="mt-5">
        <v-col
          cols="6"
          style="display: grid; justify-content: end"
          >{{ $t("views.licence.review." + mode) }}</v-col
        >
        <v-col
          cols="6"
          style="display: grid; justify-content: end">
          <s
            data-cy="old-monthly-costs"
            v-if="reviewData.oldCosts !== 0 && reviewData.oldCosts !== reviewData.newCosts"
            >{{ reviewData.oldCosts }} €</s
          >
          <span data-cy="final-monthly-costs">{{ reviewData.newCosts }} €</span>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
  <v-dialog
    v-model="dialog.open"
    persistent>
    <LicenceReviewPopup
      @close="dialog.open = false"
      @save="submit"
      :review-data="reviewData"
      :initial-items="initialItems"
      :items="items"
      :charge-needed="chargeNeeded"
      :modules-removed="modulesRemoved" />
  </v-dialog>
  <v-dialog v-model="addressDialog.open">
    <InvoiceAdressView :success="addressDialog.onSuccess" />
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, ref, Ref, toRef } from "vue";
import { useMainStore } from "@/stores/mainStore";
import { useTranslation } from "i18next-vue";
import { useModules } from "./moduleComposable";
import LicenceReviewPopup from "@/components/dialogs/LicenceReviewPopup.vue";
import { useAuthStore } from "@/stores/authStore";
import InvoiceAdressView from "@/components/dialogs/InvoiceAdressView.vue";
import { phase } from "@/types/global";

const { t } = useTranslation();
const authStore = useAuthStore();
const mainStore = useMainStore();

const dialog = ref({
  open: false,
  onSuccess: () => {}
});

const addressDialog = ref({
  open: false,
  onSuccess: () => {}
});

const props = withDefaults(
  defineProps<{
    allowedEdit?: boolean;
    current_phase: phase;
    current_phase_mode?: string;
    next_phase: phase;
    current_status?: string | null;
    licence_id?: number;
    next_billing_cycle_anchor?: number | null;
  }>(),
  {
    allowedEdit: false,
    current_phase_mode: "monthly",
    current_status: null,
    next_billing_cycle_anchor: null
  }
);

const slots = defineModel<Record<number, number>>("slots", { required: true });
const mode = defineModel<string>("mode", { default: "monthly" });

const {
  headers,
  items,
  modulesRemoved,
  chargeNeeded,
  initialItems,
  loadPreview,
  someoneLeft,
  reviewData
} = useModules(
  toRef(() => slots.value),
  props.allowedEdit,
  toRef(() => props.current_status),
  toRef(() => props.licence_id),
  toRef(() => mode.value),
  toRef(() => props.current_phase) as Ref<phase>,
  toRef(() => props.next_phase) as Ref<phase>,
  props.current_phase_mode,
  props.next_billing_cycle_anchor
);

defineExpose({
  saveModules,
  someoneLeft
});

const tempResult: ComputedRef<number> = computed(() => {
  return Object.entries(slots.value).reduce((result, [key, value]) => {
    return value ? result | Number(key) : result;
  }, 0);
});

const modulesRequirements = computed(() => {
  return Object.fromEntries(
    mainStore.modules.map((item) => [item.value, item.requires])
  );
});

function changeValue(item: number, value: number) {
  slots.value[item] = value;
  if (value === 0) {
    for (const [id, requires] of Object.entries(modulesRequirements.value)) {
      if (item & (requires || 0)) {
        changeValue(id as unknown as number, 0);
      }
    }
  }
}

function setupCustomer() {
  dialog.value.open = false;
  addressDialog.value.open = true;
}

function saveModules(callback: () => void) {
  dialog.value.open = true;
  chargeNeeded.value > 0 && loadPreview();
  if (!authStore.user.stripe) {
    dialog.value.onSuccess = setupCustomer;
    addressDialog.value.onSuccess = callback;
  } else {
    dialog.value.onSuccess = callback;
  }
}

function submit() {
  dialog.value.onSuccess();
}
</script>

<style lang="scss" scoped>
.paymentMode {
  border-radius: 30px;
  background-color: #eeeeee;
  padding: 3px;

  .modeButton {
    border-radius: 25px;
    padding: 7px 20px;
    margin: 1px 4px;
    color: #666666;
  }
  @keyframes onHover {
    0%   {color: #666666;}
    100%  {color: black;}
  }
  @keyframes onLeave {
    0%   {color: black;}
    100%  {color: #666666;}
  }
  .not-active {
    animation: onLeave 0.5s;

    &:hover {
      color: black;
      animation: onHover 0.5s;
    }
  }

  .active {
    background-color: white;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.19);
    color: black;
  }
}
</style>
